// @ts-nocheck
/**
 * Add/edit/delete dialogs for the board's elements: teams, people, initiatives, epics and tasks.
 * Teams and people live in the project config (saved with putConfig); initiatives, epics and
 * tasks are plan state and change only through PlanOps.
 */
import React, { useEffect, useMemo, useState } from "react";
import { addPerson, addTeam, fullName, personNames, projectConfigSchema, removePerson, removeTeam, updatePerson, updateTeam } from "@super-gantt/core";
import { authClient } from "../lib/auth";
import { Avatar, DiscIcon, discMeta } from "../lib/disciplines";

export const EDITOR_CSS = `
  .hov .hb{opacity:0;transition:opacity .12s}
  .hov:hover .hb,.hb:focus-visible{opacity:1}
  @media (hover:none){.hov .hb{opacity:.55}}
  .hb{background:none;border:none;color:#AEB9C8;cursor:pointer;font-size:13px;line-height:1;padding:1px 4px;border-radius:3px;flex-shrink:0}
  .hb:hover{background:#364356;color:#E8EDF3}
  .plus{background:none;border:1px dashed #364356;color:#8A97A8;border-radius:3px;cursor:pointer;font:600 10px 'Barlow Condensed';letter-spacing:.06em;padding:3px 8px;text-transform:uppercase;text-align:left}
  .plus:hover{border-color:#E8A33D;color:#E8A33D}
  .ed label{display:flex;flex-direction:column;gap:3px;font-size:11px;color:#8A97A8;margin-bottom:10px}
  .ed input:not([type=checkbox]):not([type=color]),.ed select{background:#151A21;color:#E8EDF3;border:1px solid #364356;border-radius:4px;padding:6px 8px;font-size:13px;font-family:inherit}
  .ed .row{display:flex;gap:10px}
  .ed .row>label{flex:1}
  .ed .check{flex-direction:row;align-items:center;gap:6px;color:#E8EDF3;font-size:13px}
`;

/** The on-hover ☰ that opens an item's edit dialog. Put it inside an element with class "hov". */
export function Hamburger({ onClick, title }) {
  return (
    <button className="hb" title={title} aria-label={title}
      onPointerDown={e => e.stopPropagation()}
      onClick={e => { e.stopPropagation(); onClick(); }}>☰</button>
  );
}

export function Plus({ onClick, children, style }) {
  return <button className="plus" style={style} onPointerDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); onClick(); }}>+ {children}</button>;
}

const TEAM_COLORS = ["#E8A33D", "#5FD3BC", "#7AA7E8", "#D88AD8", "#E07A6E", "#9CCB6B", "#E8D25F"];
const newId = () => `u-${crypto.randomUUID().slice(0, 8)}`;

/**
 * edit: { kind: "team"|"person"|"initiative"|"epic"|"task", mode: "add"|"edit", ... }
 * ctx: the board's live data and write helpers.
 */
export function EditDialog({ edit, ctx, onClose }) {
  const { config, state, m, resolved, act, saveConfig, displayOrg, project, wsOfTeam } = ctx;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [armed, setArmed] = useState(false); // delete needs a second click

  const backlog = state.backlog || [];
  const epic = edit.epic ? backlog.find(t => t.n === edit.epic) : null;
  const team = edit.team !== undefined ? config.teams.find(t => t.id === edit.team) : null;
  const person = edit.person ? config.resources.find(r => r.key === edit.person) : null;

  const [f, setF] = useState(() => initialFields(edit, { config, state, epic, team, person, resolved, m, wsOfTeam }));
  const set = patch => setF(x => ({ ...x, ...patch }));

  /* org members for profile linking */
  const [members, setMembers] = useState(null);
  useEffect(() => {
    if (edit.kind !== "person" || !project.organizationId) return;
    authClient.organization.getFullOrganization({ query: { organizationId: project.organizationId } })
      .then(res => setMembers(res.data?.members || []))
      .catch(() => setMembers([]));
  }, [edit.kind, project.organizationId]);

  const run = async fn => {
    setErr(""); setBusy(true);
    try {
      const ok = await fn();
      if (ok !== false) onClose();
    } catch (e) {
      setErr(e?.message || String(e));
    } finally { setBusy(false); }
  };
  /** Dispatches plan ops; dispatch reports failure through the board's error line, so a false return keeps the dialog open. */
  const ops = list => { if (!act(list, { manual: true })) throw new Error("That change couldn't be applied"); };

  let title = "", body = null, save = null, del = null, delNote = "";

  if (edit.kind === "team") {
    title = edit.mode === "add" ? "New team" : `Edit ${team.name}`;
    body = <>
      <label>Name<input autoFocus value={f.name} onChange={e => set({ name: e.target.value })} /></label>
      <label>Color<input type="color" value={f.color} onChange={e => set({ color: e.target.value })} style={{ width: 60, height: 30, background: "none", border: "none" }} /></label>
    </>;
    save = () => run(async () => {
      if (!f.name.trim()) throw new Error("Give the team a name");
      const next = edit.mode === "add" ? addTeam(config, { name: f.name, color: f.color }).config : updateTeam(config, team.id, { name: f.name, color: f.color });
      await saveConfig(next);
    });
    if (edit.mode === "edit") {
      if (team.members.length) delNote = "Move or remove its people first.";
      else if (config.teams.length === 1) delNote = "A project needs at least one team.";
      del = delNote ? null : () => run(async () => {
        const next = removeTeam(config, team.id);
        const moved = displayOrg[team.id] || [];
        if (moved.length) {
          const org = JSON.parse(JSON.stringify(displayOrg));
          delete org[team.id];
          const to = next.defaultOwnerTeam;
          org[to] = [...(org[to] || []), ...moved];
          ops({ type: "setWsOrg", org });
        }
        await saveConfig(next);
      });
    }
  }

  if (edit.kind === "person") {
    title = edit.mode === "add" ? (team ? `Add a person to ${team.name}` : "New person") : `Edit ${fullName(person)}`;
    const hasWork = edit.mode === "edit" && (resolved[person.key] || []).length > 0;
    /* adding to a team: anyone already on the project can join it, so one person can sit in several teams */
    const teamsOf = key => [config.homeTeam[key], ...config.teams.filter(t => t.members.includes(key)).map(t => t.id)]
      .filter((x, i, a) => x !== undefined && a.indexOf(x) === i);
    const others = edit.mode === "add" && team ? config.resources.filter(r => !team.members.includes(r.key)) : [];
    const joinTeam = r => run(async () => saveConfig(updatePerson(config, r.key, { teams: [...teamsOf(r.key), team.id] })));
    // a new person whose name matches someone on the project is most likely that person
    const typed = `${f.first.trim()} ${f.last.trim()}`.trim().toLowerCase();
    const sameName = edit.mode === "add" && typed ? config.resources.find(r => fullName(r).toLowerCase() === typed)
      || (!f.last.trim() ? config.resources.find(r => [personNames(r).first, r.nickname || ""].some(x => x.trim().toLowerCase() === typed)) : null) : null;
    const teamName = id => config.teams.find(t => t.id === id)?.name || "";
    body = <>
      {others.length > 0 && <>
        <div style={{ fontSize: 11, color: "#8A97A8", marginBottom: 6 }}>Someone already on the project</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14, maxHeight: 168, overflowY: "auto" }}>
          {others.map(r => (
            <button type="button" key={r.key} disabled={busy} onClick={() => joinTeam(r)}
              title={`Add ${fullName(r)} to ${team.name}. They stay in ${teamsOf(r.key).map(teamName).join(", ") || "no other team"} too.`}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "3px 10px 3px 3px", borderRadius: 16, cursor: "pointer", background: "#232B36", border: "1px solid #364356", color: "#E8EDF3", font: "600 12px 'Barlow Condensed'", letterSpacing: ".04em" }}>
              <Avatar name={fullName(r)} disc={discMeta(config, r.discipline)} size={22} badge={false} />{fullName(r)}
            </button>
          ))}
        </div>
        <div style={{ fontSize: 11, color: "#8A97A8", marginBottom: 6, borderTop: "1px solid #2E3947", paddingTop: 10 }}>Or someone new</div>
      </>}
      <div className="row">
        <label>First name<input autoFocus={!others.length} value={f.first} onChange={e => set({ first: e.target.value })} /></label>
        <label>Last name<input value={f.last} onChange={e => set({ last: e.target.value })} /></label>
      </div>
      {sameName && <div style={{ fontSize: 12, color: "#E8A33D", margin: "-4px 0 10px" }}>
        {fullName(sameName)} is already on the project{team && !team.members.includes(sameName.key) ? <>. <button type="button" className="plus" style={{ marginLeft: 4 }} onClick={() => joinTeam(sameName)}>Add them to {team.name}</button></> : team ? ` and already in ${team.name}.` : "."}
      </div>}
      <label>Nickname<input value={f.nick} placeholder="Optional" onChange={e => set({ nick: e.target.value })} />
        <span style={{ fontSize: 11, color: "#5F6B7C" }}>Shown on the board instead of their name. The avatar always uses first and last initials.</span></label>
      <div className="row">
        <label>Primary discipline
          <select value={f.discipline} disabled={hasWork} title={hasWork ? "Move their work first to change discipline" : undefined} onChange={e => set({ discipline: e.target.value, extra: f.extra.filter(x => x !== e.target.value) })}>
            {config.disciplines.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
        </label>
        <label>Time (FTE)<input type="number" min={0.1} max={1} step={0.1} value={f.fte} onChange={e => set({ fte: e.target.value })} /></label>
      </div>
      <div style={{ fontSize: 11, color: "#8A97A8", marginBottom: 4 }}>Also does</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
        {config.disciplines.filter(d => d.key !== f.discipline).map(d => {
          const dm = discMeta(config, d.key), on = f.extra.includes(d.key);
          return (
            <button type="button" key={d.key} aria-pressed={on}
              onClick={() => set({ extra: on ? f.extra.filter(x => x !== d.key) : [...f.extra, d.key] })}
              style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 9px 4px 6px", borderRadius: 14, cursor: "pointer",
                background: on ? "#232B36" : "transparent", border: `1px ${on ? "solid" : "dashed"} ${on ? dm.color : "#364356"}`,
                color: on ? "#E8EDF3" : "#8A97A8", font: "600 12px 'Barlow Condensed'", letterSpacing: ".05em", textTransform: "uppercase" }}>
              <DiscIcon icon={dm.icon} size={14} color={on ? dm.color : "#5F6B7C"} />{d.label}
            </button>
          );
        })}
      </div>
      <label>Profile
        <select value={f.userId} onChange={e => set({ userId: e.target.value })}>
          <option value="">Not linked</option>
          {(members || []).map(mb => <option key={mb.userId} value={mb.userId}>{mb.user?.name || mb.user?.email} ({mb.user?.email})</option>)}
          {f.userId && members && !members.some(mb => mb.userId === f.userId) && <option value={f.userId}>A former member</option>}
        </select>
      </label>
      <div style={{ fontSize: 11, color: "#8A97A8", marginBottom: 4 }}>Teams</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px 14px", marginBottom: 10 }}>
        {config.teams.map(t => (
          <label key={t.id} className="check" style={{ marginBottom: 0 }}>
            <input type="checkbox" checked={f.teams.includes(t.id)}
              onChange={e => set({ teams: e.target.checked ? [...f.teams, t.id] : f.teams.filter(x => x !== t.id) })} />
            <span style={{ color: t.color }}>{t.name}</span>
          </label>
        ))}
      </div>
    </>;
    save = () => run(async () => {
      if (!f.first.trim()) throw new Error("Give them a first name");
      const fte = +f.fte;
      if (!(fte > 0 && fte <= 1)) throw new Error("Time should be between 0.1 and 1");
      if (!f.teams.length) throw new Error("Pick at least one team");
      if (sameName && fullName(sameName).toLowerCase() === typed) throw new Error(`${fullName(sameName)} is already on the project. Pick them above instead of adding a second copy.`);
      const input = { firstName: f.first, lastName: f.last, nickname: f.nick, discipline: f.discipline, extraDisciplines: f.extra, fte, userId: f.userId || null, teams: f.teams };
      const next = edit.mode === "add" ? addPerson(config, input).config : updatePerson(config, person.key, input);
      await saveConfig(next);
    });
    if (edit.mode === "edit") {
      if (hasWork) delNote = "Move or delete their tasks first.";
      del = hasWork ? null : () => run(async () => saveConfig(removePerson(config, person.key)));
    }
  }

  if (edit.kind === "initiative") {
    title = edit.mode === "add" ? "New initiative" : `Edit ${edit.name}`;
    const count = backlog.filter(t => t.w.trim() === edit.name).length;
    body = <>
      <label>Name<input autoFocus value={f.name} onChange={e => set({ name: e.target.value })} /></label>
      <label>Team
        <select value={f.team} onChange={e => set({ team: +e.target.value })}>
          {config.teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </label>
    </>;
    save = () => run(() => {
      if (!f.name.trim()) throw new Error("Give the initiative a name");
      if (edit.mode === "add") ops({ type: "addInitiative", name: f.name.trim(), team: f.team });
      else {
        const op = { type: "updateInitiative", name: edit.name };
        if (f.name.trim() !== edit.name) op.newName = f.name.trim();
        if (f.team !== edit.team) op.team = f.team;
        if (op.newName || op.team !== undefined) ops(op);
      }
      ctx.afterInitiative?.(f.name.trim(), edit.mode === "add" ? undefined : edit.name);
    });
    if (edit.mode === "edit") {
      delNote = count ? `This also deletes its ${count} epic${count > 1 ? "s" : ""} and their tasks.` : "";
      del = () => run(() => { ops({ type: "deleteInitiative", name: edit.name }); ctx.afterInitiative?.(null, edit.name); });
    }
  }

  if (edit.kind === "epic") {
    title = edit.mode === "add" ? `New epic in ${edit.ws}` : `Edit epic`;
    const inits = ctx.initiatives;
    body = <>
      <label>Name<input autoFocus value={f.n} onChange={e => set({ n: e.target.value })} /></label>
      <div className="row">
        <label>Initiative
          <select value={f.w} onChange={e => set({ w: e.target.value })}>
            {inits.map(w => <option key={w} value={w}>{w}</option>)}
          </select>
        </label>
        <label>Priority
          <select value={f.p} onChange={e => set({ p: e.target.value })}>
            {config.priorities.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </label>
      </div>
      <label className="check"><input type="checkbox" checked={f.s} onChange={e => set({ s: e.target.checked })} /> In scope (budgeted)</label>
      <label>Who should do it (optional hint, e.g. a name per discipline)<input value={f.i} onChange={e => set({ i: e.target.value })} /></label>
    </>;
    save = () => run(() => {
      if (!f.n.trim()) throw new Error("Give the epic a name");
      if (edit.mode === "add") ops({ type: "addEpic", epic: { n: f.n.trim(), w: f.w, p: f.p, s: f.s, i: f.i } });
      else {
        const patch = {};
        for (const k of ["n", "w", "p", "s", "i"]) if (f[k] !== epic[k]) patch[k] = typeof f[k] === "string" ? f[k].trim() : f[k];
        if (Object.keys(patch).length) ops({ type: "updateEpic", name: epic.n, patch });
      }
    });
    if (edit.mode === "edit") {
      const n = Object.keys(epic?.e || {}).length;
      delNote = n ? `This also deletes its ${n} task${n > 1 ? "s" : ""}.` : "";
      del = () => run(() => ops({ type: "deleteEpic", name: epic.n }));
    }
  }

  if (edit.kind === "task") {
    const discs = edit.mode === "add" ? config.disciplines.filter(d => epic?.e[d.key] === undefined) : config.disciplines.filter(d => d.key === edit.disc);
    title = edit.mode === "add" ? `New task on ${edit.epic}` : `${m.discLabel(edit.disc)} on ${edit.epic}`;
    const people = config.resources.filter(r => r.discipline === f.disc);
    body = !discs.length ? <div style={{ fontSize: 13, color: "#AEB9C8", marginBottom: 10 }}>This epic already has a task in every discipline. Edit one of them instead.</div> : <>
      <div className="row">
        <label>Discipline
          <select value={f.disc} disabled={edit.mode === "edit"} onChange={e => set({ disc: e.target.value, person: pickPerson(config, resolved, e.target.value, edit.ws, wsOfTeam) })}>
            {discs.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
        </label>
        <label>Person-days<input type="number" min={0.5} step={0.5} value={f.pd} onChange={e => set({ pd: e.target.value })} /></label>
      </div>
      <label>Assigned to
        <select value={f.person} onChange={e => set({ person: e.target.value })}>
          {!people.length && <option value="">No {m.discLabel(f.disc)} people yet</option>}
          {people.map(r => <option key={r.key} value={r.key}>{r.name}</option>)}
        </select>
      </label>
      {edit.mode === "edit" && edit.chunks > 1 && <div style={{ fontSize: 11, color: "#8A97A8", marginBottom: 10 }}>This task is split into {edit.chunks} chunks; reassigning moves all of them, and person-days are the total.</div>}
    </>;
    save = !discs.length ? null : () => run(() => {
      const pd = +f.pd;
      if (!(pd >= 0.5)) throw new Error("Person-days should be at least 0.5");
      if (!f.person) throw new Error(`Add a ${m.discLabel(f.disc)} person first`);
      if (edit.mode === "add") ops({ type: "addTask", epic: edit.epic, disc: f.disc, pd, person: f.person, segId: newId() });
      else {
        const op = { type: "updateTask", epic: edit.epic, disc: edit.disc };
        if (Math.abs(pd - f.pd0) > 1e-9) op.pd = pd;
        if (f.person !== f.person0) op.person = f.person;
        if (op.pd !== undefined || op.person !== undefined) ops(op);
      }
    });
    if (edit.mode === "edit") del = () => run(() => ops({ type: "deleteTask", epic: edit.epic, disc: edit.disc }));
  }

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.6)", zIndex: 250, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <form className="ed" onClick={e => e.stopPropagation()} onSubmit={e => { e.preventDefault(); if (!busy) save?.(); }}
        onKeyDown={e => { if (e.key === "Escape") onClose(); }}
        style={{ background: "#1B222C", border: "1px solid #364356", borderRadius: 8, padding: 20, width: "min(440px, 92vw)", maxHeight: "90dvh", overflow: "auto" }}>
        <div style={{ font: "700 17px 'Barlow Condensed'", letterSpacing: ".05em", color: "#E8A33D", marginBottom: 14 }}>{title}</div>
        {body}
        {err && <div style={{ color: "#e05a4f", fontSize: 12.5, marginBottom: 10 }}>{err}</div>}
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", marginTop: 6 }}>
          {edit.mode === "edit" && (del
            ? <button type="button" className="btn" disabled={busy} style={{ borderColor: "#C0392B", color: "#e08a8a" }}
                onClick={() => (armed ? del() : setArmed(true))}>{armed ? "Really delete?" : "Delete"}</button>
            : delNote ? <span style={{ fontSize: 11, color: "#5F6B7C" }}>Can't delete: {delNote}</span> : null)}
          <div style={{ flex: 1 }} />
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          {save && <button type="submit" className="btn" disabled={busy} style={{ borderColor: "#E8A33D", color: "#E8A33D" }}>{edit.mode === "add" ? "Add" : "Save"}</button>}
        </div>
        {armed && del && delNote && <div style={{ fontSize: 11, color: "#e08a8a", marginTop: 8 }}>{delNote}</div>}
      </form>
    </div>
  );
}

/** The least-loaded person in a discipline, preferring the team that owns the initiative. */
export function pickPerson(config, resolved, disc, ws, wsOfTeam) {
  const team = ws !== undefined ? wsOfTeam?.(ws) : undefined;
  const members = new Set(config.teams.find(t => t.id === team)?.members || []);
  const cands = config.resources.filter(r => r.discipline === disc);
  const end = r => (resolved[r.key] || []).reduce((mx, s) => Math.max(mx, s.d1), 0) / (r.fte || 1);
  cands.sort((a, b) => (members.has(b.key) ? 1 : 0) - (members.has(a.key) ? 1 : 0) || end(a) - end(b));
  return cands[0]?.key || "";
}

function initialFields(edit, { config, state, epic, team, person, resolved, m, wsOfTeam }) {
  switch (edit.kind) {
    case "team":
      return edit.mode === "add"
        ? { name: "", color: TEAM_COLORS[config.teams.length % TEAM_COLORS.length] }
        : { name: team.name, color: team.color };
    case "person":
      return edit.mode === "add"
        ? { first: "", last: "", nick: "", discipline: config.disciplines[0].key, extra: [], fte: 1, userId: "", teams: edit.team !== undefined ? [edit.team] : [config.teams[0].id] }
        : {
          first: personNames(person).first, last: personNames(person).last, nick: person.nickname || "", discipline: person.discipline, extra: person.extraDisciplines || [], fte: person.fte, userId: person.userId || "",
          teams: [config.homeTeam[person.key], ...config.teams.filter(t => t.members.includes(person.key)).map(t => t.id)]
            .filter((x, i, a) => x !== undefined && a.indexOf(x) === i),
        };
    case "initiative":
      return { name: edit.name || "", team: edit.team ?? config.teams[0].id };
    case "epic": {
      if (edit.mode === "edit") return { n: epic.n, w: epic.w.trim(), p: epic.p, s: epic.s, i: epic.i || "" };
      const pr = config.priorities.filter(p => !p.excluded);
      return { n: "", w: edit.ws, p: (pr[Math.floor(pr.length / 2)] || config.priorities[0]).key, s: true, i: "" };
    }
    case "task": {
      if (edit.mode === "edit") {
        const pd = epic?.e[edit.disc] ?? edit.pd;
        return { disc: edit.disc, pd, pd0: pd, person: edit.person, person0: edit.person };
      }
      const disc = config.disciplines.find(d => epic?.e[d.key] === undefined)?.key || config.disciplines[0].key;
      return { disc, pd: 5, person: pickPerson(config, resolved, disc, edit.ws, wsOfTeam) };
    }
  }
}

/** Validates a config before saving, with a readable first error. */
export function checkConfig(next) {
  const r = projectConfigSchema.safeParse(next);
  if (!r.success) {
    const i = r.error.issues[0];
    throw new Error(`${i.path.join(".")}: ${i.message}`);
  }
  return r.data;
}
